import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { ItemForm, type ItemFormSubmitValues } from '@/components/item-form'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { isLimitReachedError } from '@/lib/limit-error'
import { trpc } from '@/trpc/client'

export function NewItemPage() {
	const navigate = useNavigate()
	const utils = trpc.useUtils()
	const [error, setError] = useState<string | null>(null)
	const create = trpc.items.create.useMutation()

	async function handleSubmit(values: ItemFormSubmitValues) {
		setError(null)
		try {
			const item = await create.mutateAsync({
				name: values.name,
				price: values.price,
				currency: values.currency,
				purchaseDate: values.purchaseDate,
				warrantyMonths: values.warrantyMonths,
				notifyBeforeExpiry: values.notifyBeforeExpiry,
				notifyDaysBefore: values.notifyDaysBefore,
				store: values.store,
				notes: values.notes,
				categoryIds: values.categoryIds,
				...(values.file ?? {}),
			})
			await Promise.all([
				utils.items.list.invalidate(),
				utils.organizations.getCurrent.invalidate(),
			])
			toast.success('Item added')
			navigate(`/app/items/${item.id}`)
		} catch (err) {
			const message =
				err instanceof Error ? err.message : 'Could not save the item'
			setError(message)
			if (isLimitReachedError(err)) {
				toast.error(message, {
					action: {
						label: 'Upgrade',
						onClick: () => navigate('/app/settings/billing'),
					},
				})
			}
		}
	}

	return (
		<div className="space-y-8">
			<PageHeader
				title="Add a new item"
				description="Track a new receipt or invoice and its warranty."
				actions={
					<Button asChild variant="ghost">
						<Link to="/app">Back to items</Link>
					</Button>
				}
			/>
			<ItemForm
				defaultValues={{}}
				submitLabel="Save item"
				onSubmit={handleSubmit}
				pending={create.isPending}
				error={error}
				onCancel={() => navigate('/app')}
				autoFillOnUpload
			/>
		</div>
	)
}
